import {
  type CoreAdminProps,
  CustomRoutes,
  localStorageStore,
  type AuthProvider,
  Resource,
} from "ra-core";
import { Route } from "react-router";
import { useEffect, useMemo } from "react";
import { Admin } from "@/components/admin/admin";

import { Dashboard } from "../dashboard/Dashboard";
import { ResponsiveLayout } from "../layout/ResponsiveLayout";
import { StartPage } from "../login/StartPage";
import {
  authProvider as defaultAuthProvider,
  dataProvider as defaultDataProvider,
} from "../providers/fakerest";
import contacts from "../resources/contacts";
import jobs from "../resources/jobs";
import proposals from "../resources/proposals";
import invoices from "../resources/invoices";
import activities from "../resources/activities";
import { SettingsPage } from "../settings/SettingsPage";
import { ProfilePage } from "../settings/ProfilePage";
import { SalesShow } from "../sales/SalesShow";
import {
  CONFIGURATION_STORE_KEY,
  type ConfigurationContextValue,
} from "./ConfigurationContext";
import type { CrmDataProvider } from "../providers/types";
import {
  defaultTitle,
  defaultLightModeLogo,
  defaultDarkModeLogo,
} from "./defaultConfiguration";
import { i18nProvider } from "../providers/commons/i18nProvider";

const defaultStore = localStorageStore(undefined, "CRM");

export type CRMProps = {
  dataProvider?: CrmDataProvider;
  authProvider?: AuthProvider;
  disableTelemetry?: boolean;
  store?: CoreAdminProps["store"];
} & Partial<ConfigurationContextValue>;

export const CRM = ({
  title = defaultTitle,
  darkModeLogo = defaultDarkModeLogo,
  lightModeLogo = defaultLightModeLogo,
  dataProvider = defaultDataProvider,
  authProvider = defaultAuthProvider,
  store = defaultStore,
  disableTelemetry,
  ...rest
}: CRMProps) => {
  useEffect(() => {
    if (
      disableTelemetry ||
      process.env.NODE_ENV !== "production" ||
      typeof window === "undefined" ||
      typeof window.location === "undefined" ||
      typeof Image === "undefined"
    ) {
      return;
    }
    const img = new Image();
    img.src = `https://finturf-crm-telemetry.marmelab.com/finturf-crm-telemetry?domain=${window.location.hostname}`;
  }, [disableTelemetry]);

  // Seed the store with CRM prop values if not already stored
  if (!store.getItem(CONFIGURATION_STORE_KEY)) {
    store.setItem(CONFIGURATION_STORE_KEY, {
      title,
      darkModeLogo,
      lightModeLogo,
    } satisfies Partial<ConfigurationContextValue>);
  }

  const wrappedAuthProvider = useMemo<AuthProvider>(
    () => ({
      ...authProvider,
      login: async (params: any) => {
        const result = await authProvider.login(params);
        try {
          const config = await dataProvider.getConfiguration();
          if (Object.keys(config).length > 0) {
            store.setItem(CONFIGURATION_STORE_KEY, config);
          }
        } catch {
          // Non-critical
        }
        return result;
      },
      logout: async (params: any) => {
        try {
          store.removeItem(CONFIGURATION_STORE_KEY);
        } catch {
          // Ignore
        }
        return authProvider.logout(params);
      },
    }),
    [authProvider, dataProvider, store],
  );

  return (
    <Admin
      layout={ResponsiveLayout}
      dashboard={Dashboard}
      dataProvider={dataProvider}
      authProvider={wrappedAuthProvider}
      i18nProvider={i18nProvider}
      store={store}
      loginPage={StartPage}
      requireAuth
      disableTelemetry
      {...rest}
    >
      <CustomRoutes>
        <Route path={SettingsPage.path} element={<SettingsPage />} />
        <Route path={ProfilePage.path} element={<ProfilePage />} />
        <Route path="/sales/:id/show" element={<SalesShow />} />
      </CustomRoutes>
      <Resource name="contacts" {...contacts} />
      <Resource name="jobs" {...jobs} />
      <Resource name="proposals" {...proposals} />
      <Resource name="invoices" {...invoices} />
      <Resource name="activities" {...activities} />
      <Resource name="sales" />
    </Admin>
  );
};
